import type {
  AlertDto,
  AnalyticsDailyResponse,
  HistorySearchResponse,
  MailboxMetricsResponse,
  OpsResponse,
  TimelineItem,
  TimelineResponse,
} from '@platform/contracts';
import { domainFromEmail, variantLabel } from '@platform/contracts';
import {
  AlertsIllustration,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EChart,
  EmptyState,
  ErrorState,
  IconBadge,
  type IconBadgeProps,
  Input,
  Monogram,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@platform/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  BellOff,
  BellRing,
  Bot,
  CalendarClock,
  Check,
  CheckCheck,
  Clock,
  Cpu,
  Download,
  Eye,
  Maximize2,
  FileText,
  FlaskConical,
  Inbox,
  Layers,
  Mail,
  Megaphone,
  Radio,
  Search,
  ShieldAlert,
  TriangleAlert,
  UserRound,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { type ReactNode, type SubmitEvent, useId, useState } from 'react';
import { toast } from 'sonner';

import { StoreLogo, useTenantTag } from '../components/company';
import { AbTests, InstantlyTotals, TrackedRate, TotalsFootnote } from '../components/metrics';
import { Guard, PageHeader } from '../components/page';
import { QualifyBudgetCard } from '../components/qualify-budget';
import { ViewerDialog } from '../components/viewer';
import { api, errorMessage } from '../lib/api';
import {
  EVENT_LABELS,
  fmtBytes,
  fmtDateTime,
  fmtDay,
  fmtDuration,
  fmtNum,
  fmtPct,
  useScopedQuery,
  useTenantName,
} from '../lib/console';
import { areaFill, baseChart, useChartColors } from '../lib/charts';
import { fmtAgo } from '../lib/format';
import { useMe } from '../lib/queries';
import { buildScopeOptions } from '../lib/scope';
import { useScope } from '../lib/use-scope';

const BOUNCE_THRESHOLD = 0.04;
const RANGES = [7, 30, 90] as const;

export function useScopeLabel(): string {
  const me = useMe();
  const { scope } = useScope();
  return buildScopeOptions(me).find((o) => o.value === scope)?.label ?? 'Todas las empresas';
}

/** Selector de periodo (control segmentado). */
export function RangePicker({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <div
      className="inline-flex rounded-full border bg-card p-1 shadow-card"
      role="group"
      aria-label="Periodo"
    >
      {RANGES.map((d) => (
        <button
          key={d}
          type="button"
          aria-pressed={d === days}
          onClick={() => {
            onChange(d);
          }}
          className={cn(
            'h-8 rounded-full px-3.5 text-sm font-medium tabular-nums transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
            d === days
              ? 'bg-primary text-primary-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.14)]'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {d} días
        </button>
      ))}
    </div>
  );
}

function QueryState({
  isLoading,
  error,
  children,
}: {
  isLoading: boolean;
  error: unknown;
  children: ReactNode;
}) {
  if (isLoading)
    return (
      <div aria-busy="true" aria-label="Cargando" className="grid gap-4">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  if (error) return <ErrorState message={errorMessage(error)} />;
  return <>{children}</>;
}

function Kpi({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'brand',
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  icon: LucideIcon;
  tone?: IconBadgeProps['tone'];
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border bg-card p-4 shadow-card">
      <IconBadge tone={tone}>
        <Icon />
      </IconBadge>
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-display text-2xl leading-tight font-semibold tabular-nums">{value}</p>
        {hint ? <p className="text-xs font-medium text-destructive">{hint}</p> : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Analítica

export function AnalyticsPage() {
  const label = useScopeLabel();
  const { theme, colors } = useChartColors();
  const [days, setDays] = useState(30);
  const daily = useScopedQuery<AnalyticsDailyResponse>(
    ['analytics', days],
    `/api/analytics/daily?days=${String(days)}`,
  );
  const boxes = useScopedQuery<MailboxMetricsResponse>(
    ['mailboxes', days],
    `/api/analytics/mailboxes?days=${String(days)}`,
  );
  const tenantTag = useTenantTag();
  const d = daily.data;
  const base = baseChart(colors);

  const volumeOption = d && {
    ...base,
    grid: { left: 40, right: 16, top: 44, bottom: 28 },
    legend: {
      top: 0,
      left: 0,
      icon: 'circle',
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 18,
      textStyle: { color: colors.axis, fontSize: 12 },
    },
    xAxis: { ...base.xAxis, data: d.daily.map((x) => fmtDay(x.date)) },
    series: [
      {
        type: 'bar',
        name: 'Enviados',
        data: d.daily.map((x) => x.sent),
        barMaxWidth: 12,
        itemStyle: { color: colors.series[0], borderRadius: [6, 6, 2, 2], opacity: 0.9 },
      },
      // Sin seguimiento de aperturas la línea sería siempre 0: no se dibuja.
      ...(d.openTracking.status === 'none'
        ? []
        : [
            {
              type: 'line',
              name: 'Aperturas',
              data: d.daily.map((x) => x.uniqueOpened),
              smooth: 0.35,
              showSymbol: false,
              symbolSize: 8,
              lineStyle: { width: 2 },
              itemStyle: { color: colors.series[2], borderColor: colors.surface, borderWidth: 2 },
            },
          ]),
      {
        type: 'line',
        name: 'Respuestas',
        data: d.daily.map((x) => x.uniqueReplies),
        smooth: 0.35,
        showSymbol: false,
        symbolSize: 8,
        lineStyle: { width: 2 },
        itemStyle: { color: colors.series[1], borderColor: colors.surface, borderWidth: 2 },
      },
    ],
  };
  const bounceOption = d && {
    ...base,
    grid: { left: 48, right: 16, top: 16, bottom: 28 },
    tooltip: {
      ...base.tooltip,
      axisPointer: { type: 'line', lineStyle: { color: colors.axis, type: 'dashed' } },
      valueFormatter: (v: number) => fmtPct(v),
    },
    xAxis: { ...base.xAxis, data: d.daily.map((x) => fmtDay(x.date)) },
    yAxis: {
      ...base.yAxis,
      minInterval: 0,
      min: 0,
      max: (v: { max: number }) => Math.max(0.06, Math.ceil(v.max * 120) / 100),
      axisLabel: { color: colors.axis, formatter: (v: number) => fmtPct(v) },
    },
    series: [
      {
        type: 'line',
        name: 'Rebote',
        data: d.daily.map((x) => (x.sent ? x.bounced / x.sent : 0)),
        smooth: 0.3,
        showSymbol: false,
        symbolSize: 8,
        lineStyle: { width: 2 },
        areaStyle: { color: areaFill(colors.series[3], 0.18) },
        itemStyle: { color: colors.series[3], borderColor: colors.surface, borderWidth: 2 },
        markLine: {
          symbol: 'none',
          lineStyle: { color: colors.axis, type: 'dashed' },
          data: [{ yAxis: BOUNCE_THRESHOLD, name: 'Límite 4 %' }],
          label: { formatter: 'Límite 4 %', color: colors.axis, position: 'insideEndTop' },
        },
      },
    ],
  };

  return (
    <Guard requires="data:read">
      <PageHeader
        title="Resultados"
        description={`${label}. Envíos, aperturas y respuestas día a día, según Instantly (se actualiza cada hora).`}
        actions={<RangePicker days={days} onChange={setDays} />}
      />
      {d?.campaigns.some((c) => c.totals.sequencesStarted > 0) ? (
        <section aria-labelledby="por-campana" className="mb-8 flex flex-col gap-4">
          <h2 id="por-campana" className="text-base font-semibold">
            Cada campaña, como la ve Instantly
          </h2>
          {d.campaigns
            .filter((c) => c.totals.sequencesStarted > 0)
            .map((c) => (
              <Card key={c.campaignId}>
                <CardHeader className="flex-row flex-wrap items-center gap-x-3 gap-y-1">
                  <CardTitle icon={<Megaphone />}>{c.name}</CardTitle>
                  {tenantTag(c.tenantSlug)}
                </CardHeader>
                <CardContent>
                  <InstantlyTotals totals={c.totals} compact />
                </CardContent>
              </Card>
            ))}
          <TotalsFootnote
            updatedAt={
              d.campaigns
                .map((c) => c.totals.updatedAt)
                .filter((x): x is string => x !== null)
                .sort()
                .at(-1) ?? null
            }
          />
        </section>
      ) : null}
      <QueryState isLoading={daily.isLoading} error={daily.error}>
        {d ? (
          <div className="grid gap-6 xl:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle icon={<Mail />}>Envíos y respuestas</CardTitle>
                <CardDescription>
                  Personas distintas que abrieron o respondieron cada día
                </CardDescription>
              </CardHeader>
              <CardContent>
                {volumeOption ? (
                  <EChart
                    theme={theme}
                    option={volumeOption}
                    role="img"
                    aria-label="Envíos, aperturas y respuestas por día"
                  />
                ) : null}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle icon={<ShieldAlert />}>Emails que rebotan</CardTitle>
                <CardDescription>
                  Porcentaje de emails que no llegaron. Por encima del 4 % los envíos se frenan
                  solos.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {bounceOption ? (
                  <EChart
                    theme={theme}
                    option={bounceOption}
                    role="img"
                    aria-label="Porcentaje de rebote por día"
                  />
                ) : null}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </QueryState>
      <Tabs defaultValue="campaigns" className="mt-8">
        <TabsList>
          <TabsTrigger value="campaigns">
            <Megaphone aria-hidden /> Por campaña
          </TabsTrigger>
          <TabsTrigger value="ab">
            <FlaskConical aria-hidden /> Pruebas A/B
          </TabsTrigger>
          <TabsTrigger value="mailboxes">
            <Inbox aria-hidden /> Por buzón
          </TabsTrigger>
        </TabsList>
        <TabsContent value="campaigns">
          <DataTable
            empty="Todavía no hay resultados por campaña. Llegan cada noche desde Instantly."
            numericFrom={2}
            head={[
              'Campaña',
              'Empresa',
              'Enviados',
              'Apertura',
              'Respuesta',
              'Rebote',
              'Interesados',
            ]}
            rows={(d?.campaigns ?? []).map((c) => [
              <span key="n" className="font-medium">
                {c.name}
              </span>,
              tenantTag(c.tenantSlug),
              fmtNum(c.sent),
              <TrackedRate
                key="o"
                rate={c.totals.openRate}
                tracking={c.totals.openTracking}
                kind="opens"
              />,
              fmtPct(c.sent ? c.uniqueReplies / c.sent : null),
              fmtPct(c.sent ? c.bounced / c.sent : null),
              fmtNum(c.opportunities),
            ])}
          />
        </TabsContent>
        <TabsContent value="ab">
          {d && d.abTests.length > 0 ? (
            <div className="flex flex-col gap-3">
              <p className="max-w-3xl text-sm text-muted-foreground">
                Cada email de la secuencia puede tener varias versiones (A, B, C…) que Instantly
                reparte por igual. Aquí se ve cuál consigue más respuestas; solo se marca una
                ganadora cuando cada versión lleva al menos 100 envíos y la diferencia es clara.
              </p>
              <AbTests
                tests={d.abTests}
                showCampaign={new Set(d.abTests.map((t) => t.campaignId)).size > 1}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Todavía no hay pruebas A/B. Llegan desde Instantly cada hora.
            </p>
          )}
        </TabsContent>
        <TabsContent value="mailboxes">
          <DataTable
            empty="Todavía no hay buzones conectados."
            numericFrom={3}
            head={[
              'Buzón',
              'Empresa',
              'Estado',
              'Calentamiento',
              'Límite diario',
              'Enviados',
              'Rebote',
            ]}
            rows={(boxes.data ?? []).map((b) => [
              <span key="m" className="inline-flex items-center gap-2">
                <Monogram name={b.mailbox} size="xs" className="rounded-full" />
                {b.mailbox}
              </span>,
              tenantTag(b.tenantSlug),
              b.status !== null && b.status < 0 ? (
                <Badge key="s" variant="destructive">
                  Con problemas
                </Badge>
              ) : (
                <Badge key="s" variant="success">
                  Funcionando
                </Badge>
              ),
              b.warmupScore === null ? '—' : `${String(Math.round(b.warmupScore))} / 100`,
              b.dailyLimit === null ? '—' : String(b.dailyLimit),
              fmtNum(b.sent),
              fmtPct(b.sent ? b.bounced / b.sent : null),
            ])}
          />
        </TabsContent>
      </Tabs>
    </Guard>
  );
}

/** Un ícono por origen de la ejecución, para distinguirlos de un vistazo. */
const RUN_SOURCE_ICON: Record<string, LucideIcon> = { n8n: Workflow, cron: Clock, worker: Cpu };

function DataTable({
  head,
  rows,
  empty,
  bare = false,
  numericFrom,
}: {
  head: string[];
  rows: ReactNode[][];
  empty: string;
  /** Desde esta columna, cifras alineadas a la derecha (cabecera incluida). */
  numericFrom?: number;
  /** Dentro de una tarjeta: sin borde ni sombra propios. */
  bare?: boolean;
}) {
  if (rows.length === 0) {
    return (
      <p className="flex items-center justify-center gap-2 rounded-xl border border-dashed bg-card/60 px-4 py-8 text-center text-sm text-muted-foreground">
        <Layers className="size-4 shrink-0" aria-hidden />
        {empty}
      </p>
    );
  }
  return (
    <div
      className={
        bare
          ? '-mx-5 -mb-5 border-t [&>div]:rounded-none [&>div]:border-0 [&>div]:shadow-none'
          : undefined
      }
    >
      <Table>
        <TableHeader>
          <TableRow>
            {head.map((h, j) => (
              <TableHead
                key={h || String(j)}
                className={numericFrom !== undefined && j >= numericFrom ? 'text-right' : undefined}
              >
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {r.map((c, j) => (
                <TableCell
                  key={j}
                  className={
                    numericFrom !== undefined && j >= numericFrom
                      ? 'text-right tabular-nums'
                      : j >= 2
                        ? 'tabular-nums'
                        : undefined
                  }
                >
                  {c}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ------------------------------------------------------------------ Historial

export function HistoryPage() {
  const [q, setQ] = useState('');
  const [email, setEmail] = useState<string | null>(null);
  const inputId = useId();
  const search = useScopedQuery<HistorySearchResponse>(
    ['history-search', q],
    `/api/history/search?q=${encodeURIComponent(q)}`,
    { enabled: q.length >= 2 },
  );
  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = new FormData(e.currentTarget).get('q');
    setQ(typeof value === 'string' ? value.trim() : '');
    setEmail(null);
  };
  return (
    <Guard requires="data:read">
      <PageHeader
        title="Historial de emails"
        description="Todo lo que se le envió a un contacto y lo que respondió, con los adjuntos exactos."
      />
      <form onSubmit={onSubmit} className="mb-6 flex max-w-xl gap-2" role="search">
        <label htmlFor={inputId} className="sr-only">
          Buscar lead
        </label>
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            id={inputId}
            name="q"
            placeholder="Email, nombre de la tienda o web"
            minLength={2}
            className="h-10 pl-9"
          />
        </div>
        <Button type="submit" className="h-10">
          Buscar
        </Button>
      </form>
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div>
          {q.length < 2 ? (
            <EmptyState
              icon={<Search />}
              title="Busca un contacto"
              description="Escribe su email, el nombre de la tienda o su web para ver cada email enviado y cada respuesta."
            />
          ) : (
            <QueryState isLoading={search.isLoading} error={search.error}>
              {(search.data ?? []).length === 0 ? (
                <EmptyState
                  icon={<Search />}
                  title={`Nadie coincide con «${q}»`}
                  description="Prueba con parte del email o de la web."
                />
              ) : (
                <ul className="flex flex-col gap-1">
                  {(search.data ?? []).map((r) => (
                    <li key={`${r.tenantSlug}:${r.email}`}>
                      <button
                        type="button"
                        onClick={() => {
                          setEmail(r.email);
                        }}
                        aria-pressed={email === r.email}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-xl border bg-card px-3 py-2.5 text-left text-sm shadow-card transition-[background-color,border-color] outline-none hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring',
                          email === r.email
                            ? 'border-highlight ring-1 ring-highlight'
                            : 'border-border',
                        )}
                      >
                        <StoreLogo
                          name={r.company ?? r.email}
                          colorKey={r.company ?? r.email}
                          domain={domainFromEmail(r.email)}
                          size="sm"
                        />
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{r.email}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {[
                              r.company,
                              r.tenantName,
                              `${String(r.messages)} ${r.messages === 1 ? 'mensaje' : 'mensajes'}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </QueryState>
          )}
        </div>
        <div>{email ? <Timeline email={email} /> : null}</div>
      </div>
    </Guard>
  );
}

function Timeline({ email }: { email: string }) {
  const tenantName = useTenantName();
  const q = useScopedQuery<TimelineResponse>(
    ['timeline', email],
    `/api/history/timeline?email=${encodeURIComponent(email)}`,
  );
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {q.data ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <StoreLogo
              name={q.data.leads[0]?.company ?? q.data.email}
              colorKey={q.data.leads[0]?.company ?? q.data.email}
              domain={domainFromEmail(q.data.email) ?? q.data.leads[0]?.url}
              size="lg"
            />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold break-all">{q.data.email}</h2>
              {q.data.leads.map((l) => (
                <p key={l.id} className="text-sm text-muted-foreground">
                  {[
                    l.company,
                    tenantName(l.tenantSlug),
                    l.qualified === null
                      ? null
                      : l.qualified
                        ? 'encaja con el perfil'
                        : 'descartada',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              ))}
            </div>
          </div>
          <TimelineList items={q.data.items} />
        </div>
      ) : null}
    </QueryState>
  );
}

/** Mensajes (con cuerpo y PDF exacto) y eventos de un lead, en orden. Reutilizado por Inbox y Leads. */
export function TimelineList({ items }: { items: readonly TimelineItem[] }) {
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Inbox />}
        title="Todavía no hay mensajes"
        description="Cuando se le envíe un email o responda, aparecerá aquí."
      />
    );
  }
  return (
    <ol
      className="relative flex flex-col gap-3 before:absolute before:top-2 before:bottom-2 before:left-[1.15rem] before:w-px before:bg-border"
      aria-label="Historial"
    >
      {items.map((item) => (
        <li key={`${item.kind}:${item.id}`} className="relative">
          {item.kind === 'message' ? <MessageCard m={item} /> : <EventRow e={item} />}
        </li>
      ))}
    </ol>
  );
}

const CHANNEL_LABELS: Record<string, string> = {
  instantly: 'Campaña',
  gmail: 'Gmail',
  manual: 'Manual',
};

type MessageItem = Extract<TimelineItem, { kind: 'message' }>;
type EventItem = Extract<TimelineItem, { kind: 'event' }>;

function MessageCard({ m }: { m: MessageItem }) {
  const [pdf, setPdf] = useState<{ url: string; filename: string } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const out = m.direction === 'out';
  return (
    <div className="flex gap-3">
      <IconBadge
        tone={out ? 'cyan' : 'green'}
        className="relative z-10 mt-4 ring-4 ring-background"
      >
        {out ? <ArrowUpRight /> : <ArrowDownLeft />}
      </IconBadge>
      <Card className="min-w-0 flex-1">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant={m.direction === 'out' ? 'default' : 'secondary'}>
              {m.direction === 'out' ? 'Enviado' : 'Recibido'}
            </Badge>
            <Badge variant="outline">{CHANNEL_LABELS[m.channel] ?? m.channel}</Badge>
            {m.origin === 'bot' ? (
              <Badge variant="success">
                <Bot aria-hidden /> Asistente
              </Badge>
            ) : m.origin === 'manual' ? (
              <Badge variant="outline">
                <UserRound aria-hidden /> Respuesta de una persona
              </Badge>
            ) : null}
            {m.status === 'failed' ? <Badge variant="destructive">No se envió</Badge> : null}
            {m.isAutoReply ? <Badge variant="warning">Respuesta automática</Badge> : null}
            <span title={fmtDateTime(m.at)}>{fmtAgo(m.at)}</span>
            {m.campaignName ? <span>· {m.campaignName}</span> : null}
            {m.step !== null ? (
              <span>
                · Paso {m.step}
                {m.variant !== null ? ` · Variante ${variantLabel(m.variant - 1)}` : ''}
              </span>
            ) : null}
            {m.direction === 'out' && m.untracked ? (
              <Badge variant="outline">Sin seguimiento de aperturas</Badge>
            ) : null}
            {m.mailbox ? <span>· desde {m.mailbox}</span> : null}
          </div>
          <CardTitle className="text-base">{m.subject ?? '(sin asunto)'}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {m.bodyHtml ? (
            <div className="flex flex-col gap-2">
              {/* HTML de terceros: iframe sin scripts ni mismo origen; la CSP de la SPA bloquea
                  imágenes remotas (pixels de tracking incluidos). */}
              <iframe
                title={`Cuerpo: ${m.subject ?? m.id}`}
                sandbox=""
                srcDoc={m.bodyHtml}
                className="h-64 w-full rounded-md border border-border bg-white"
              />
              <Button
                size="sm"
                variant="ghost"
                className="self-end"
                onClick={() => {
                  setExpanded(true);
                }}
              >
                <Maximize2 /> Ver email completo
              </Button>
              <ViewerDialog
                open={expanded}
                onOpenChange={setExpanded}
                title={m.subject ?? '(sin asunto)'}
                description={`${m.direction === 'out' ? 'Enviado' : 'Recibido'} · ${fmtDateTime(m.at)}`}
              >
                <iframe
                  title={`Email completo: ${m.subject ?? m.id}`}
                  sandbox=""
                  srcDoc={m.bodyHtml}
                  className="size-full"
                />
              </ViewerDialog>
            </div>
          ) : m.bodyText ? (
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-sm">
              {m.bodyText}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">No se guardó el texto de este email.</p>
          )}
          {m.attachments.length ? (
            <div className="flex flex-col gap-2">
              {m.attachments.map((a) => {
                const url = `/api/attachments/${a.id}`;
                return (
                  <div
                    key={a.id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 p-2.5 text-sm"
                  >
                    <IconBadge tone="destructive" size="sm">
                      <FileText />
                    </IconBadge>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{a.filename}</span>
                      <span className="text-xs text-muted-foreground" title={`sha256 ${a.sha256}`}>
                        {fmtBytes(a.sizeBytes)}
                      </span>
                    </span>
                    {a.mime === 'application/pdf' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPdf({ url, filename: a.filename });
                        }}
                      >
                        <Eye /> Ver PDF
                      </Button>
                    ) : null}
                    <Button asChild size="sm" variant="ghost">
                      <a href={url} download={a.filename}>
                        <Download /> Descargar
                      </a>
                    </Button>
                  </div>
                );
              })}
              <ViewerDialog
                open={pdf !== null}
                onOpenChange={(v) => {
                  if (!v) setPdf(null);
                }}
                title={pdf?.filename ?? 'PDF'}
                description="PDF enviado"
                {...(pdf ? { download: { href: pdf.url, filename: pdf.filename } } : {})}
              >
                {pdf ? <iframe title="PDF enviado" src={pdf.url} className="size-full" /> : null}
              </ViewerDialog>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function EventRow({ e }: { e: EventItem }) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-0.5 text-xs text-muted-foreground">
      <span className="relative z-10 flex w-9 shrink-0 justify-center" aria-hidden>
        <span
          className={cn(
            'size-2.5 rounded-full ring-4 ring-background',
            e.eventType === 'email_bounced' ? 'bg-destructive' : 'bg-muted-foreground/50',
          )}
        />
      </span>
      <Badge variant={e.eventType === 'email_bounced' ? 'destructive' : 'outline'}>
        {EVENT_LABELS[e.eventType] ?? e.eventType}
      </Badge>
      <span title={fmtDateTime(e.at)}>{fmtAgo(e.at)}</span>
      {e.campaignName ? <span>· {e.campaignName}</span> : null}
    </div>
  );
}

// ------------------------------------------------------------------ Operaciones

const STATUS_VARIANT = {
  success: 'success',
  error: 'destructive',
  warning: 'warning',
  running: 'default',
  canceled: 'secondary',
  unknown: 'secondary',
} as const;

const RUN_STATUS_LABEL: Record<keyof typeof STATUS_VARIANT, string> = {
  success: 'bien',
  error: 'error',
  warning: 'aviso',
  running: 'en curso',
  canceled: 'cancelado',
  unknown: 'sin dato',
};

export function OpsPage() {
  const tenantTag = useTenantTag();
  const [source, setSource] = useState<string>('all');
  const sourceId = useId();
  const q = useScopedQuery<OpsResponse>(
    ['ops', source],
    `/api/ops${source === 'all' ? '' : `?source=${source}`}`,
    { requires: 'ops:read' },
  );
  const d = q.data;
  return (
    <Guard requires="ops:read">
      <PageHeader
        title="Estado del sistema"
        description="Ejecuciones de n8n, crons y jobs del worker con el error legible, latidos de los crons y estado de la ingesta de eventos."
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d ? (
          <>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Kpi icon={Inbox} label="Eventos en cola" value={fmtNum(d.ingest.pendingRaw)} />
              <Kpi
                icon={TriangleAlert}
                tone={d.ingest.failedRaw > 0 ? 'destructive' : 'neutral'}
                label="Eventos con error (24 h)"
                value={fmtNum(d.ingest.failedRaw)}
              />
              <Kpi
                icon={Radio}
                tone="green"
                label="Último evento de Instantly"
                value={fmtAgo(d.ingest.lastEventAt)}
              />
              <Kpi
                icon={Clock}
                tone={d.heartbeats.some((h) => h.overdue) ? 'warning' : 'neutral'}
                label="Crons atrasados"
                value={fmtNum(d.heartbeats.filter((h) => h.overdue).length)}
              />
            </div>
            <QualifyBudgetCard />
            <Card className="mt-6 overflow-hidden">
              <CardHeader>
                <CardTitle icon={<CalendarClock />}>Tareas programadas</CardTitle>
                <CardDescription>
                  Cada cron avisa al terminar; si no avisa a tiempo, queda atrasado.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DataTable
                  bare
                  empty="Ninguna tarea programada avisó todavía (POST /api/ingest/heartbeat)."
                  head={['Tarea', 'Empresa', 'Último aviso', 'Estado', 'Duración', 'Cada']}
                  rows={d.heartbeats.map((h) => [
                    <span key="j" className="font-medium">
                      {h.jobKey}
                    </span>,
                    tenantTag(h.tenantSlug),
                    <span key="t" className="whitespace-nowrap" title={fmtDateTime(h.lastSeenAt)}>
                      {fmtAgo(h.lastSeenAt)}
                    </span>,
                    h.overdue ? (
                      <Badge key="s" variant="destructive">
                        atrasado
                      </Badge>
                    ) : (
                      <Badge key="s" variant={STATUS_VARIANT[h.lastStatus ?? 'unknown']}>
                        {RUN_STATUS_LABEL[h.lastStatus ?? 'unknown']}
                      </Badge>
                    ),
                    fmtDuration(h.lastDurationMs),
                    fmtDuration(h.expectedIntervalSeconds * 1000),
                  ])}
                />
              </CardContent>
            </Card>
            <Card className="mt-6 overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <CardTitle icon={<Workflow />}>Ejecuciones</CardTitle>
                <div className="w-40">
                  <label htmlFor={sourceId} className="sr-only">
                    Origen
                  </label>
                  <Select value={source} onValueChange={setSource}>
                    <SelectTrigger id={sourceId}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="n8n">n8n</SelectItem>
                      <SelectItem value="cron">Crons</SelectItem>
                      <SelectItem value="worker">Worker</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <DataTable
                  bare
                  empty="Todavía no hay ejecuciones registradas."
                  head={['Inicio', 'Origen', 'Tarea', 'Empresa', 'Estado', 'Duración', 'Error']}
                  rows={d.runs.map((r) => [
                    <span key="t" className="whitespace-nowrap" title={fmtDateTime(r.startedAt)}>
                      {fmtAgo(r.startedAt)}
                    </span>,
                    <span key="o" className="inline-flex items-center gap-1.5">
                      {(() => {
                        const SourceIcon = RUN_SOURCE_ICON[r.source] ?? Activity;
                        return (
                          <SourceIcon className="size-3.5 text-muted-foreground" aria-hidden />
                        );
                      })()}
                      {`${r.source}${r.instance ? ` · ${r.instance}` : ''}`}
                    </span>,
                    r.job,
                    tenantTag(r.tenantSlug),
                    <Badge key="s" variant={STATUS_VARIANT[r.status]}>
                      {RUN_STATUS_LABEL[r.status]}
                    </Badge>,
                    fmtDuration(r.durationMs),
                    r.error ? (
                      <span
                        className="line-clamp-2 max-w-md text-xs text-destructive"
                        title={r.error}
                      >
                        {r.error}
                      </span>
                    ) : (
                      '—'
                    ),
                  ])}
                />
              </CardContent>
            </Card>
          </>
        ) : null}
      </QueryState>
    </Guard>
  );
}

// ------------------------------------------------------------------ Alertas

const SEVERITY_VARIANT = {
  info: 'secondary',
  warning: 'warning',
  critical: 'destructive',
} as const;
const SEVERITY_LABEL = { info: 'Informativa', warning: 'Aviso', critical: 'Urgente' } as const;

export function AlertsPage() {
  const me = useMe();
  const tenantTag = useTenantTag();
  const [status, setStatus] = useState<'open' | 'resolved'>('open');
  const qc = useQueryClient();
  const q = useScopedQuery<AlertDto[]>(['alerts', status], `/api/alerts?status=${status}`, {
    requires: 'ops:read',
  });
  const canResolve = me.user.isSuperadmin || me.permissions.includes('config:write');
  const resolve = useMutation({
    mutationFn: (id: string) => api(`/api/alerts/${id}/resolve`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Alerta resuelta');
      void qc.invalidateQueries({ queryKey: ['console'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Guard requires="ops:read">
      <PageHeader
        title="Alertas"
        description="Rebote alto, buzones con problemas, tareas que no avisan o flujos que fallan. Las urgentes llegan también por Telegram."
      />
      <Tabs
        value={status}
        onValueChange={(v) => {
          setStatus(v === 'resolved' ? 'resolved' : 'open');
        }}
      >
        <TabsList>
          <TabsTrigger value="open">
            <BellRing aria-hidden /> Abiertas
          </TabsTrigger>
          <TabsTrigger value="resolved">
            <CheckCheck aria-hidden /> Resueltas
          </TabsTrigger>
        </TabsList>
        <TabsContent value={status} className="mt-4">
          <QueryState isLoading={q.isLoading} error={q.error}>
            {(q.data ?? []).length === 0 ? (
              <EmptyState
                {...(status === 'open'
                  ? { illustration: <AlertsIllustration /> }
                  : { icon: <BellOff /> })}
                title={
                  status === 'open' ? 'No hay alertas abiertas' : 'Todavía no se resolvió ninguna'
                }
                description={status === 'open' ? 'Todo funciona con normalidad.' : undefined}
              />
            ) : (
              <DataTable
                empty=""
                head={['Importancia', 'Alerta', 'Empresa', 'Veces', 'Última vez', 'Avisada', '']}
                rows={(q.data ?? []).map((a) => [
                  <Badge key="s" variant={SEVERITY_VARIANT[a.severity]}>
                    {SEVERITY_LABEL[a.severity]}
                  </Badge>,
                  <span key="t">
                    <span className="block font-medium">{a.title}</span>
                    <span className="text-xs text-muted-foreground">{a.ruleKey}</span>
                  </span>,
                  tenantTag(a.tenantSlug),
                  fmtNum(a.occurrences),
                  fmtAgo(a.status === 'open' ? a.lastSeenAt : a.resolvedAt),
                  a.notifiedAt ? fmtAgo(a.notifiedAt) : '—',
                  a.status === 'open' && canResolve ? (
                    <Button
                      key="r"
                      size="sm"
                      variant="outline"
                      disabled={resolve.isPending}
                      onClick={() => {
                        resolve.mutate(a.id);
                      }}
                    >
                      <Check /> Resolver
                    </Button>
                  ) : null,
                ])}
              />
            )}
          </QueryState>
        </TabsContent>
      </Tabs>
    </Guard>
  );
}
